import type { Notification } from '@pm/schemas';
import { Button, cn, Counter, LabelCaps, Popover, PopoverContent, PopoverTrigger } from '@pm/ui';
import { Bell, CheckCheck, Settings2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { api } from '@/lib/api';
import { fmtRelative } from '@/lib/format';
import { keys, useInvalidating, useNotifications, useUnreadCount } from '@/lib/queries';

/** Sino da topbar: últimas notificações in-app com marcação de leitura (API §17). */
export function NotificationsMenu() {
  const navigate = useNavigate();
  const { data: unread } = useUnreadCount();
  const { data } = useNotifications(false);
  const items = data?.data ?? [];
  const inval = [['notifications'], keys.unread];
  const readOne = useInvalidating((id: string) => api.post(`/notifications/${id}/read`), inval);
  const readAll = useInvalidating(() => api.post('/notifications/read-all'), inval);
  const count = unread?.count ?? 0;

  function open(n: Notification) {
    if (!n.read_at) readOne.mutate(n.id);
    if (n.entity_type === 'task' && n.entity_id) void navigate(`/tasks/${n.entity_id}`);
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Notificações${count ? ` (${count} não lidas)` : ''}`}
          className="relative"
        >
          <Bell />
          {count > 0 ? (
            <Counter className="absolute -right-1.5 -top-1.5">{count > 99 ? '99+' : count}</Counter>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0">
        <header className="flex items-center gap-2 border-b border-glass-border px-3 py-2">
          <LabelCaps>Notificações</LabelCaps>
          <span className="ml-auto flex items-center gap-1">
            {count > 0 ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={() => readAll.mutate(undefined)}
              >
                <CheckCheck /> Marcar todas
              </Button>
            ) : null}
            <Link
              to="/settings/notifications"
              className="inline-flex size-6 items-center justify-center rounded text-text-3 hover:bg-surface-2 hover:text-text-1"
              aria-label="Preferências de notificação"
              title="Preferências"
            >
              <Settings2 className="size-3.5" />
            </Link>
          </span>
        </header>
        <ul className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-text-3">Nada por aqui.</li>
          ) : null}
          {items.map((n) => (
            <li key={n.id}>
              <button
                type="button"
                onClick={() => open(n)}
                className={cn(
                  'flex w-full flex-col gap-0.5 border-b border-border px-3 py-2 text-left hover:bg-surface-2',
                  !n.read_at && 'bg-accent-soft/40',
                )}
              >
                <span className="flex items-center gap-2">
                  {!n.read_at ? <span className="size-1.5 rounded-full bg-accent" /> : null}
                  <span
                    className={cn('flex-1 text-sm', !n.read_at ? 'font-medium' : 'text-text-2')}
                  >
                    {n.title}
                  </span>
                  <span className="font-mono text-[10px] text-text-3">
                    {fmtRelative(n.created_at)}
                  </span>
                </span>
                {n.body ? <span className="line-clamp-2 text-xs text-text-3">{n.body}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
